import React, { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth-context";
import { formatTime } from "../../lib/format";

import { useTranslation } from "react-i18next";

const BANNER_HEIGHT = "2.5rem";

/**
 * Shown while a super admin works in a club through break-glass access: which
 * club, why, until when, and one click to leave. When the grant runs out the
 * server already refuses everything, so the banner leaves on its own.
 */
export function ClubAccessBanner() {
  const { t } = useTranslation("common");
  const { user, leaveClub } = useAuth();
  const grant = user?.accessGrant ?? null;
  const [leaving, setLeaving] = useState(false);

  const leave = async () => {
    setLeaving(true);
    try {
      await leaveClub();
    } finally {
      window.location.href = "/super-admin";
    }
  };

  useEffect(() => {
    if (!grant) return;
    const remaining = new Date(grant.expiresAt).getTime() - Date.now();
    const timer = setTimeout(leave, Math.max(0, remaining));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grant?.id, grant?.expiresAt]);

  // The app header and sticky page bars sit below the banner: they read its height from --banner-h.
  useEffect(() => {
    if (!grant) return;
    const root = document.documentElement;
    root.style.setProperty("--banner-h", BANNER_HEIGHT);
    return () => {
      root.style.removeProperty("--banner-h");
    };
  }, [!!grant]);

  if (!grant) return null;

  const time = formatTime(grant.expiresAt);

  return (
    <div
      className="sticky top-0 z-40 flex items-center gap-2 bg-destructive px-4 text-sm font-medium text-destructive-foreground sm:justify-center sm:gap-3"
      style={{ height: BANNER_HEIGHT }}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 truncate sm:hidden">{grant.club.name}</span>
      <span className="shrink-0 whitespace-nowrap sm:hidden">· {t("clubAccessBanner.untilShort", { time })}</span>
      <span className="hidden min-w-0 truncate sm:inline">{t("clubAccessBanner.inClub", { club: grant.club.name, time })}</span>
      {grant.reason && (
        <span className="hidden min-w-0 truncate font-normal opacity-90 md:inline" title={grant.reason}>
          {t("clubAccessBanner.reason", { reason: grant.reason })}
        </span>
      )}
      <Button size="sm" variant="secondary" onClick={leave} disabled={leaving} className="ml-auto h-7 shrink-0 px-2.5 sm:ml-0">
        <span className="sm:hidden">{t("clubAccessBanner.leaveShort")}</span>
        <span className="hidden sm:inline">{t("clubAccessBanner.leave")}</span>
      </Button>
    </div>
  );
}
